'use client';

// ---------------------------------------------------------------------------
// MusicCompactPanel — the small player
// ---------------------------------------------------------------------------
//
// The middle of the three states: track name, transport, a seek bar, volume, and
// a control to expand. Positioned `fixed` rather than as a dropdown anchored to
// the trigger, because the Topbar unmounts when you cross route groups and an
// anchored panel would disappear mid-track.
//
// `end-*`, not `right-*`: Arabic is RTL, and a hard-coded right edge would put
// the panel over the sidebar there.
//
// WHICH CORNER, and why not the obvious one. `CallsCenterFloating`'s header says
// toasts own bottom-centre and bottom-right — that is STALE. The toast container
// is `fixed top-4 right-4 z-[100]` (ToastProvider.tsx:66), i.e. TOP-right. So the
// natural spot under the trigger, `top-16 end-4`, sits directly beneath the toast
// stack, and at z-[100] against this panel's z-[60] a toast covers the player's
// own header — including the error toasts the player itself raises, hiding the
// controls needed to react to them.
//
// Verified positions of everything that floats:
//   top-4    right-4    z-[100]  toasts            (ToastProvider.tsx:66)
//   bottom-4 left-1/2   z-[120]  UpdateBanner      (UpdateBanner.tsx:68)
//   bottom-4 left-4     z-40     CallsCenterFloating
//   right edge, transient        call drawers
//
// Bottom-END is the one free region, and it pairs with the calls dock at
// bottom-start. Toasts stay above this panel, which is what you want.
// ---------------------------------------------------------------------------

import {
  ChevronDown,
  Maximize2,
  Music2,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
} from 'lucide-react';

import { useTranslation } from '@/core/i18n';
import { cn } from '@/core/utils/cn';

import { useStockLibraryIntoStore } from '../hooks/useMusicLibrary';
import { formatDuration, isLocalTrack, trackSubtitle } from '../lib/track';
import {
  selectCanGoNext,
  selectCanGoPrevious,
  selectCanPlay,
  selectCurrentTrack,
  useMusicStore,
} from '../stores/music.store';

export function MusicCompactPanel() {
  const { t } = useTranslation();
  // So play can start the library before the expanded panel has ever opened.
  useStockLibraryIntoStore();

  const track = useMusicStore(selectCurrentTrack);
  const status = useMusicStore((s) => s.status);
  const positionSec = useMusicStore((s) => s.positionSec);
  const durationSec = useMusicStore((s) => s.durationSec);
  const volume = useMusicStore((s) => s.volume);
  const muted = useMusicStore((s) => s.muted);
  const canPlay = useMusicStore(selectCanPlay);
  const canNext = useMusicStore(selectCanGoNext);
  const canPrev = useMusicStore(selectCanGoPrevious);

  const togglePlay = useMusicStore((s) => s.togglePlay);
  const next = useMusicStore((s) => s.next);
  const previous = useMusicStore((s) => s.previous);
  const seek = useMusicStore((s) => s.seek);
  const setVolume = useMusicStore((s) => s.setVolume);
  const toggleMute = useMusicStore((s) => s.toggleMute);
  const setPanel = useMusicStore((s) => s.setPanel);

  const isPlaying = status === 'playing';
  const isBusy = status === 'loading';

  return (
    <div
      id="music-compact-panel"
      role="region"
      aria-label={t('music.title')}
      className={cn(
        'fixed bottom-4 end-4 z-[60] w-[min(22rem,calc(100vw-2rem))]',
        'rounded-xl border border-hms-border bg-hms-card',
        'shadow-2xl shadow-black/30 backdrop-blur-xl',
        'animate-fadeIn',
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-hms-border">
        <Music2 size={14} strokeWidth={2} className="text-hms-teal flex-shrink-0" />
        <span className="text-[11px] font-semibold text-hms-textDim uppercase tracking-wider flex-1">
          {t('music.title')}
        </span>
        <button
          id="music-compact-expand"
          onClick={() => setPanel('expanded')}
          aria-label={t('music.expand')}
          title={t('music.expand')}
          className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-text hover:bg-white/[0.05] transition-all duration-200"
        >
          <Maximize2 size={13} strokeWidth={2} />
        </button>
        <button
          id="music-compact-close"
          onClick={() => setPanel('closed')}
          aria-label={t('music.minimize')}
          title={t('music.minimize')}
          className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-text hover:bg-white/[0.05] transition-all duration-200"
        >
          <ChevronDown size={14} strokeWidth={2} />
        </button>
      </div>

      {/* Now playing */}
      <div className="px-3 pt-3">
        {track ? (
          <>
            <p
              id="music-compact-title"
              dir="auto"
              className="text-[13px] font-medium text-hms-text truncate"
              title={track.title}
            >
              {track.title}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              {trackSubtitle(track) && (
                <span dir="auto" className="text-[11px] text-hms-textDim truncate">
                  {trackSubtitle(track)}
                </span>
              )}
              {isLocalTrack(track) && (
                // Says plainly that this file lives on one machine, so nobody
                // wonders why a colleague cannot hear it.
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-hms-teal/15 text-hms-teal flex-shrink-0">
                  {t('music.deviceOnly')}
                </span>
              )}
            </div>
          </>
        ) : (
          <p className="text-[12px] text-hms-textDim">
            {canPlay ? t('music.pressPlayToStart') : t('music.emptyQueue')}
          </p>
        )}
      </div>

      {/* Seek */}
      <div className="px-3 pt-2.5">
        <input
          id="music-compact-seek"
          type="range"
          min={0}
          max={durationSec ?? 0}
          value={Math.min(positionSec, durationSec ?? 0)}
          step={1}
          // Disabled until a duration is known: a range with max=0 would jump to
          // the end on any drag.
          disabled={!durationSec}
          onChange={(e) => seek(Number(e.target.value))}
          aria-label={t('music.seek')}
          className="w-full h-1 accent-hms-teal cursor-pointer disabled:cursor-default disabled:opacity-40"
        />
        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] text-hms-textDim tabular-nums">
            {formatDuration(positionSec)}
          </span>
          <span className="text-[10px] text-hms-textDim tabular-nums">
            {formatDuration(durationSec)}
          </span>
        </div>
      </div>

      {/* Transport */}
      <div className="flex items-center justify-center gap-1 px-3 pb-1">
        <button
          id="music-compact-prev"
          onClick={previous}
          disabled={!canPrev}
          aria-label={t('music.previous')}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-hms-textSub hover:text-hms-text hover:bg-white/[0.05] disabled:opacity-30 disabled:hover:bg-transparent transition-all duration-200"
        >
          <SkipBack size={16} strokeWidth={2} />
        </button>

        <button
          id="music-compact-play"
          onClick={togglePlay}
          disabled={!canPlay}
          aria-label={isPlaying ? t('music.pause') : t('music.play')}
          aria-busy={isBusy}
          className="flex items-center justify-center w-11 h-11 rounded-full bg-hms-teal/15 text-hms-teal hover:bg-hms-teal/25 disabled:opacity-30 transition-all duration-200"
        >
          {isPlaying ? <Pause size={18} strokeWidth={2} /> : <Play size={18} strokeWidth={2} />}
        </button>

        <button
          id="music-compact-next"
          onClick={() => next(false)}
          disabled={!canNext}
          aria-label={t('music.next')}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-hms-textSub hover:text-hms-text hover:bg-white/[0.05] disabled:opacity-30 disabled:hover:bg-transparent transition-all duration-200"
        >
          <SkipForward size={16} strokeWidth={2} />
        </button>
      </div>

      {/* Volume */}
      <div className="flex items-center gap-2 px-3 pb-3">
        <button
          id="music-compact-mute"
          onClick={toggleMute}
          aria-label={muted ? t('music.unmute') : t('music.mute')}
          className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-text transition-all duration-200 flex-shrink-0"
        >
          {muted || volume === 0 ? (
            <VolumeX size={14} strokeWidth={2} />
          ) : (
            <Volume2 size={14} strokeWidth={2} />
          )}
        </button>
        <input
          id="music-compact-volume"
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={muted ? 0 : volume}
          onChange={(e) => setVolume(Number(e.target.value))}
          aria-label={t('music.volume')}
          className="flex-1 h-1 accent-hms-teal cursor-pointer"
        />
      </div>
    </div>
  );
}
